import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useDresser } from '../../../../contexts/DrawerContext';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import OpenInFullPage from '../../../generic/Drawer/actions/OpenInFullPage';
import ShareLink from '../../../generic/Drawer/actions/ShareLink';
import IssueDetailsModalsProvider from '../IssueDetailsModalsContext';
import type { AppDispatch, ReduxStore } from '../../../../redux/store';

import { ISSUE_DRAWER_SLIDE_DURATION } from './constants';
import IssueDetailsDrawerContent from './IssueDetailsDrawerContent';

/**
 * Hook that provides a function to open the issue details drawer using the Dresser pattern.
 * This handles fetching issue details and pushing the content to the Dresser.
 *
 * @example
 * const openIssueDrawer = useOpenIssueDrawer();
 *
 * <button onClick={() => openIssueDrawer(issueId)}>
 *   View Issue
 * </button>
 */
export const useOpenIssueDrawer = (issueId: number) => {
  const dispatch = useDispatch<AppDispatch>();

  const { replaceDrawer } = useDresser();
  const category = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const issueDetailsURL = useSelector((state: ReduxStore) =>
    IssuesV2Store.selectors.getIssueDetailsURL(state, issueId),
  );

  return useCallback(async () => {
    // Fetch the issue details and comments
    await dispatch(IssuesV2Store.actions.fetchIssueDetails(issueId));
    dispatch(IssuesV2Store.actions.fetchIssueDetailsComments());

    // Create the onClose handler
    const onCloseDrawer = () => {
      dispatch(IssuesV2Store.actions.clearIssueDetails());
    };

    // Open the drawer with static buttons in buttonsSlot
    replaceDrawer(
      <IssueDetailsModalsProvider>
        <IssueDetailsDrawerContent category={category} issueId={issueId} />
      </IssueDetailsModalsProvider>,
      {
        onClose: onCloseDrawer,
        slideDuration: ISSUE_DRAWER_SLIDE_DURATION,
        buttonsSlot: (
          <>
            <OpenInFullPage url={issueDetailsURL} />
            <ShareLink url={issueDetailsURL} />
          </>
        ),
        shouldSkipClickOutside: true,
      },
    );
  }, [dispatch, replaceDrawer, category, issueDetailsURL, issueId]);
};
